import { useEffect, useRef, useState } from "react";
import {
  AssessmentButton,
  AssessmentCheckboxRadio,
  InputField,
  showToast,
} from "ui-components";
import {
  integrationsInfo,
  updateIntegrationDetails,
} from "../../../../service/Settings/Settings";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CopyVector from "../../../../utils/Images/CopyVector";
import { copyToClipboard } from "../../../../utils/Common/Constant";
import { SettingAppEnums } from "../../../../utils/Enums/SettingAppEnums";

const urlRegex =
  /^(https?:\/\/)?(www\.)?((([0-9]{1,3}\.){3}[0-9]{1,3})|([a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+))(:[0-9]{1,5})?(\/[^\s]*)?$/;

interface IntegrationSectionProps {
  setIntegrationModal?: any;
  shouldListRender?: any;
  dataForEdit?: any;
  setEditIntegrationModal?: any;
  setRenderDetails?: any;
  setCurrentPage?: any;
  getAppList: any;
}

const AppCreation = ({
  setIntegrationModal,
  shouldListRender,
  dataForEdit,
  setEditIntegrationModal,
  setRenderDetails,
  setCurrentPage,
  getAppList,
}: IntegrationSectionProps) => {
  const [addedName, setAddedName] = useState("");
  const [domainText, setDomainText] = useState("");
  const [addedKeywords, setAddedKeywords] = useState<string[]>([]);
  const [selectedModules, setSelectedModules] = useState<string[]>(["JOB"]);
  const [domainError, setDomainError] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [secretData, setSecretData] = useState("");
  const { t } = useTranslation();
  const urlRef = useRef(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (dataForEdit) {
      setAddedName(dataForEdit?.appName || "");
      setAddedKeywords(dataForEdit?.domain || []);
      setSelectedModules(dataForEdit?.module || []);
    }
  }, [dataForEdit]);
  const addDomain = () => {
    const trimmed = domainText.trim();
    if (!trimmed) return;

    if (!urlRegex.test(trimmed)) {
      showToast("Please enter a valid domain URL", "error");
      return;
    }

    setAddedKeywords((prev) => [...prev, trimmed]);
    setDomainText("");
  };
  function handleModuleChange(moduleName: string, checked: boolean) {
    setSelectedModules((prev) => {
      const updated = checked
        ? [...prev, moduleName]
        : prev.filter((item) => item !== moduleName);

      return updated;
    });
  }

  const handleDeleteSkillList = (ind: number) => {
    setAddedKeywords((prevData: any) => {
      const newData = prevData.filter((_: any, index: number) => index !== ind);
      return newData;
    });
  };

  const handleSaveIntegration = async () => {
    const payload = {
      appName: addedName,
      scopes: ["read"],
      domain: addedKeywords,
      module: selectedModules,
      jobCard: SettingAppEnums.JOBCARD_HTML_ENUM,
      style: SettingAppEnums.JOBCARD_CSS_ENUM,
      pagination: SettingAppEnums.JOBCARD_PAGINATION_ENUM,
      id: 1,
      search: SettingAppEnums.JOBCARD_SEARCH_ENUM,
    };

    try {
      const response = await integrationsInfo(payload);
      if (response?.status == 200) {
        showToast(response?.customMessage, "success");
        setSecretData(response?.data?.data?.clientSecret);
        setShowSecret(true);
        setCurrentPage(1);
        setAddedName("");
        setAddedKeywords([]);
        setSelectedModules([]);
        shouldListRender(Math.random() * 10);
      }
    } catch (error) {
      console.error("API call failed:", error);
    }
  };
  const handleEditIntegration = async () => {
    const payload = {
      appName: addedName,
      scopes: ["read"],
      domain: addedKeywords,
      module: selectedModules,
    };

    try {
      const response = await updateIntegrationDetails(
        dataForEdit?.clientId,
        payload,
      );
      if (response?.status == 200) {
        showToast(response?.customMessage, "success");
        setIntegrationModal && setIntegrationModal(null);
        setEditIntegrationModal && setEditIntegrationModal(false);
        setRenderDetails(Math.random() * 10);
        getAppList();
      }
    } catch (error) {
      console.error("API call failed:", error);
    }
  };
  const handleClosePopup = () => {
    setShowSecret(false);
    setIntegrationModal(false);
  };

  return (
    <div className="integrationSection">
      {/* App Name */}
      <div className="addNameSec">
        <div className="integrationSubHead">
          <h2>{t("SETTING.INTEGRATION_NAME")}</h2>
          <InputField
            value={addedName}
            additionalProps={{ ref: firstInputRef }}
            onChange={(e: any) => setAddedName(e)}
            placeholder={t("SETTING.GIVE_NAME_TO_YOUR_INTEGRATION")}
          />
        </div>
      </div>

      {/* Domain */}
      <div className="addDomainSec">
        <div className="integrationSubHead">
          <h2>{t("SETTING.DOMAIN_RESTRICTIONS")}</h2>
        </div>
        <div className="d-flex gap-2">
          <div className="w-100">
            <InputField
              value={domainText}
              onChange={(e: any) => {
                const value = e;
                setDomainText(value);
                setDomainError(
                  value.trim() === "" || urlRegex.test(value.trim())
                    ? ""
                    : t("SETTING.INVALID_DOMAIN_FORMAT"),
                );
              }}
              placeholder={t("SETTING.ENTER_DOMAIN")}
              additionalProps={{
                className: "textInput",
              }}
            />
            {domainError && <div className="errorMessage">{domainError}</div>}
          </div>

          <AssessmentButton onClick={addDomain} disabled={!domainText.trim()}>
            {t("GENERAL.ADD")}
          </AssessmentButton>
        </div>

        <div className="domainList">
          {addedKeywords.map((item, ind) => (
            <div key={ind} className="word">
              <span>{item}</span>
              <div
                onClick={() => handleDeleteSkillList(ind)}
                className="deleteKeyword"
              >
                {t("GENERAL.REMOVE")}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Features */}
      <div className="featureList">
        <div className="integrationSubHead">
          <h2>
            {t("SETTING.FEATURES")}
            {""} *
          </h2>
        </div>
        <div className="featureCheck">
          <AssessmentCheckboxRadio
            type="checkbox"
            checked={selectedModules.includes("JOB")}
            onChange={(checked) => handleModuleChange("JOB", checked)}
            label={t("SETTING.CAREER_PAGE")}
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="integrationSaveBtn">
        <AssessmentButton
          onClick={dataForEdit ? handleEditIntegration : handleSaveIntegration}
        >
          {dataForEdit ? t("GENERAL.UPDATE") : t("GENERAL.ADD")}
        </AssessmentButton>
      </div>

      <CustomModal
        show={showSecret}
        handleClose={handleClosePopup}
        isHeaderVisible={true}
        isCrossBtn={true}
        isFooterVisible={false}
        instructionHeadTitle={t("SETTING.CLIENT_SECRET_KEY")}
      >
        <div className="secretKeyTab d-flex flex-column align-items-center ">
          <h5>{t("SETTING.SECRET_KEY_DESCRIPTION")}</h5>

          <div className="scriptFieldSec mt-3">
            <div className="scriptField">
              <p ref={urlRef} title={t("SETTING.CLIENT_ID")}>
                {secretData}
              </p>
            </div>
            <div className="d-flex gap-2">
              <div className="cursorPointer" title={t("SETTING.CLIENT_ID")}>
                <AssessmentButton
                  className="lightBtn"
                  onClick={() =>
                    copyToClipboard(
                      urlRef,
                      secretData,
                      t("SETTING.SECRET_KEY_COPIED"),
                    )
                  }
                >
                  <CopyVector />
                </AssessmentButton>
              </div>
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default AppCreation;
